import React, { useEffect, useMemo } from 'react';
import KeywordCardAnalysis from '../KeywordAndURLAnalysis/pages/KeywordCardAnalysis';
import { useKeywordAnalysisDetail } from '../KeywordAndURLAnalysis/hook/useTopicAnalysisFlow';
import CardCommon from '@op/components/CardCommon';
import TimezoneTips from '@op/components/TimezoneTips';
import {
  keywordDrillUrlHeaderTips,
  KeywordTopicPreviewsTaggedTips,
} from '@op/components/BubbleCommon';

interface IKeywordAnalysisEmbed {
  /** 业务分类：keyword / keyword_topic */
  category: 'keyword' | 'keyword_topic';
  /** 后端统一列表项原始数据（含 object_type / object_id / category / status / version_id ...） */
  ext: any;
  gameInfo: any;
  wholeGameInfo: any;
  userInfo: any;
  /** 隐藏筛选器底部操作按钮（确定 / 重置 / Stop），用于只读内嵌场景 */
  hideFilterOperateBtn?: boolean;
  /** 全部预览模式，预览模式下筛选器会有特殊样式调整（FilterBox: dbo-filterbox--all-preview） */
  allPreview?: boolean;
}

/**
 * AIInsightsReport 内嵌渲染：keyword / topic 例行分析。
 *
 * 复用 KeywordAndURLAnalysis 下钻页的 KeywordCardAnalysis，以 isPreviews 纯展示模式挂载：
 *  - category = 'keyword_topic' 时走 isTopicAnalysis 分支
 *  - 只读：isCreator = false（禁用编辑 / 重置 / 保存 / Stop 等操作）
 *
 * 字段从后端统一列表项 ext 解析（object_id / version_id / data_type / short_url ...）。
 * 具体 key 待后端口径对齐后可能调整，此处做防御性兜底（object_id ?? task_id ?? id）。
 *
 * TODO（只读化，后续对接）：
 *  isPreviews 模式仍会渲染预览筛选器（KeywordPreviewFilter），它是 filterValue 的初始化数据源，
 *  直接隐藏会导致 body 不渲染。要实现「筛选器不可动」需配合 FilterFixed 的只读/禁用模式
 *  （或隐藏操作按钮 keyOperateShowOptions），属于下钻组件侵入式改造，待后续处理。
 */
export default function KeywordAnalysisEmbed({
  category,
  ext,
  gameInfo,
  wholeGameInfo,
  userInfo,
  hideFilterOperateBtn = true,
  allPreview = true,
}: IKeywordAnalysisEmbed) {
  const dataType = (ext?.data_type ?? 'new') as 'new' | 'legacy';
  const versionId = ext?.version_id;
  // 详情请求所需的 task_id 取值规则：
  //  - legacy 且 keyword：取列表项 id（旧数据 keyword 的 id 即分析任务 id）
  //  - 其余（含 keyword_topic、new keyword）：维持原 lead_id 口径
  // 旧实现统一取 ext.lead_id，legacy keyword 取到 undefined → Number(undefined) 为 NaN →
  // 请求永不发起，loading 卡在 true，CardCommon 永久转圈且不挂载 body。
  const rawLeadId =
    dataType === 'legacy' && category === 'keyword' ? ext?.id : ext?.lead_id;

  // 与下钻页 PreviewAnalysis 一致：挂载即发起一次 keyword_analysis_task_info 优先请求，
  // 首次响应即停（callbackStop: !!data），用于从 detail 派生 topic 判定及 header 相关字段。
  // body 内部仍由 LoadingCard 自有的轮询实例驱动渲染，此处仅做页面级字段对齐。
  const { requestAnalysisDetail, detail, loading } =
    useKeywordAnalysisDetail({
      needRead: true,
    });

  useEffect(() => {
    const taskIdForRequest = Number(rawLeadId);
    if (taskIdForRequest && gameInfo?.edition_unified_id) {
      const params: any = {
        task_id: taskIdForRequest,
        data_type: dataType,
        ...gameInfo,
      };
      if (versionId) {
        params.version_id = versionId;
      }
      requestAnalysisDetail(params, {
        callbackStop: (data) => !!data,
      });
    }
  }, [gameInfo, rawLeadId]);

  const {
    realLeadId,
    taskName,
    shortId,
    isTopicAnalysis,
    isSubscription,
    successTagged,
    replaceParams,
    originalShortUrl,
    shortUrl,
    createTime,
  } = useMemo(() => {
    // 逐字段对齐 PreviewAnalysis 的派出口径（PreviewAnalysis.tsx useMemo）
    const {
      keyword_analysis_task_category: taskCategory,
      keyword_analysis_task_type: taskType,
      keyword_analysis_task_id: taskId,
      id: detailId,
      data_type: detailDataType,
      task_name: taskName,
      short_url: shortUrl,
      original_short_url: originalShortUrl,
      has_tagged_result: hasTaggedResult,
      replace_params: replaceParams,
      create_time: createTime,
    } = detail || {};

    const realLeadId = detailDataType === 'new' ? taskId : detailId;
    const isTopicAnalysis = detail
      ? taskCategory === 'topic'
      : category === 'keyword_topic'; // 首屏（detail 未到）用 category 兜底，保证即时正确渲染
    const isSubscription = taskType === 'recurring';
    const successTagged = hasTaggedResult;
    const shortId = shortUrl;

    return {
      realLeadId,
      taskName,
      shortId,
      isTopicAnalysis,
      isSubscription,
      successTagged,
      replaceParams,
      originalShortUrl,
      shortUrl,
      createTime,
    };
  }, [detail]);

  // 与下钻页 PreviewAnalysis 的 raceLampTips 同口径（keyword/topic 场景，无 url 分支）：
  // loading → 无；successTagged → 打标提示；订阅例行 → 无；其余 → 基于 createTime 的报告来源提示。
  // 复用 TimezoneTips 渲染；父级 ContentBody 未传 horseRaceLampTips，故该提示只在此内嵌页出现。
  // noShowTimeZoneTips：时区等系统提示已由外层 ContentBody 负责，此处不再重复渲染。
  function raceLampTips() {
    if (loading) return undefined;
    if (successTagged) return KeywordTopicPreviewsTaggedTips;
    if (isSubscription) return undefined;
    return keywordDrillUrlHeaderTips(createTime);
  }
  const horseRaceLampTips = raceLampTips();

  return (
    <>
      <TimezoneTips
        wholeGameInfo={wholeGameInfo}
        horseRaceLampTips={horseRaceLampTips}
        // noShowTimeZoneTips
      />
      <CardCommon contentCard loading={loading} loadingHeight={400}>
        <KeywordCardAnalysis
          gameInfo={gameInfo}
          wholeGameInfo={wholeGameInfo}
          userInfo={userInfo}
          isPreviews
          isCreator={false}
          leadId={realLeadId ?? rawLeadId}
          shortId={shortId}
          dataType={dataType}
          versionId={versionId}
          isTopicAnalysis={isTopicAnalysis}
          isSubscription={isSubscription}
          successTagged={successTagged}
          replaceParams={replaceParams}
          originalShortUrl={originalShortUrl || shortUrl}
          title={taskName}
          hideFilterOperateBtn={hideFilterOperateBtn}
          allPreview={allPreview}
        />
      </CardCommon>
    </>
  );
}
